import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useChatActions } from "../../contexts/chat-actions-context";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import type {
	ContentFormat,
	EducationContent,
} from "../../services/educationService";
import styles from "./ContentCard.module.css";
import { ViewAsDropdown } from "./ViewAsDropdown";

const RECOMMENDED_BACKGROUNDS = [
	"/images/education-recommended-1.png",
	"/images/education-recommended-2.png",
];

interface ContentCardProps {
	content: EducationContent;
	index?: number;
}

export function ContentCard({ content, index = 0 }: ContentCardProps) {
	const { logEvent } = useAnalytics();
	const { setPrefillQuestion } = useChatActions();
	const navigate = useNavigate();
	const { t, direction } = useClientTranslations("education");
	const [isBookmarked, setIsBookmarked] = useState(content.isBookmarked);

	const handleAskEve = useCallback(() => {
		logEvent("education_card_ask_eve_pressed", undefined, {
			contentId: content.id,
		});
		setPrefillQuestion(content.title);
		navigate("/chat");
	}, [logEvent, content.id, setPrefillQuestion, content.title, navigate]);

	const handleBookmarkToggle = useCallback(() => {
		setIsBookmarked((prev) => {
			logEvent("education_card_bookmark_toggled", undefined, {
				contentId: content.id,
				isBookmarked: String(!prev),
			});
			return !prev;
		});
	}, [logEvent, content.id]);

	const handleShare = useCallback(() => {
		toast.info(t("actions.shareComingSoon"));
		logEvent("education_card_share_pressed", undefined, {
			contentId: content.id,
		});
	}, [t, logEvent, content.id]);

	const handleFormatSelect = useCallback(
		(format: ContentFormat) => {
			toast.info(t("actions.viewingAs", { format: t(`formats.${format}`) }));
			logEvent("education_card_view_as_selected", undefined, {
				contentId: content.id,
				format,
			});
		},
		[t, logEvent, content.id],
	);

	return (
		<div className={styles.card} dir={direction}>
			<div
				className={styles.thumbnailArea}
				style={{
					backgroundImage: `url(${RECOMMENDED_BACKGROUNDS[index % RECOMMENDED_BACKGROUNDS.length]})`,
				}}
				aria-hidden="true"
			/>
			<div className={styles.info}>
				<div className={styles.actionsRow}>
					<button
						type="button"
						className={styles.actionButton}
						onClick={handleAskEve}
						title={t("actions.askEve")}
					>
						<img
							src="/images/education/icon-ask-eve.svg"
							alt={t("actions.askEve")}
							className={styles.actionIcon}
						/>
					</button>
					<button
						type="button"
						className={`${styles.actionButton} ${isBookmarked ? styles.bookmarkActive : ""}`}
						onClick={handleBookmarkToggle}
						title={t(isBookmarked ? "actions.bookmarked" : "actions.bookmark")}
					>
						<img
							src={
								isBookmarked
									? "/images/education/icon-heart-filled.svg"
									: "/images/education/icon-heart.svg"
							}
							alt={t(isBookmarked ? "actions.bookmarked" : "actions.bookmark")}
							className={styles.actionIcon}
						/>
					</button>
					<button
						type="button"
						className={styles.actionButton}
						onClick={handleShare}
						title={t("actions.share")}
					>
						<img
							src="/images/education/icon-share.svg"
							alt={t("actions.share")}
							className={styles.actionIcon}
						/>
					</button>
				</div>
				<div className={styles.textContent}>
					<h3 className={styles.title}>{content.title}</h3>
					<p className={styles.description}>{content.description}</p>
				</div>
				<ViewAsDropdown
					availableFormats={content.availableFormats}
					onFormatSelect={handleFormatSelect}
				/>
			</div>
		</div>
	);
}
